import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { actions, ACTIONS_TYPES } from 'redux/local';
import { Paper } from '@mui/material';

import { useNotification } from 'screens/shared/hooks/notifications';
import Form from 'screens/shared/form';
import Icon from 'screens/shared/icon';
import ConfirmDialog from 'screens/shared/confirm-dialog';

import {
  _t, enumToArray, hasKeys,
} from 'utils';
import { getResourceLimitsForContract } from 'utils/contract';
import {
  SERVER_LIMITATION, CPU_FAMILY, AVAILABILITY_ZONE, STORAGE_TYPE, SERVER_TYPE, AVAILABILITY_ZONE$,
} from 'constant';
import CreateCdRom from './cd-rom';
import CreateStorage from './storage';
import CreateNic from './nic';
import Cube from './cube';

export default ({ history = {}, match }) => {
  const { dcId } = match.params;
  const [anchorEl, setAnchorEl] = useState(null);
  const [showProvisionDialog, setShowProvisionDialog] = useState(false);
  const [storages, setStorages] = useState([]);
  const [cdRoms, setCdRoms] = useState([]);
  const [nics, setNics] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [server, setServer] = useState({
    name: '', availabilityZone: AVAILABILITY_ZONE.AUTO, cpuFamily: '', cores: 1, ram: 1, idCounter: 0,
  });

  const {
    [ACTIONS_TYPES.DATA_CENTER.GET]: datacenter = {},
    [ACTIONS_TYPES.CONTRACT.GET_ALL]: contracts = [],
    templates = [],
  } = useSelector((e) => e);
  const { entities: dcEntities = {}, properties = {} } = datacenter;
  const { lans = {} } = dcEntities;
  const { items: lanItems = [] } = lans;
  const { cpuArchitecture = [] } = properties;
  const [{ maxCores = 1, maxRam = 1024 } = {}] = cpuArchitecture;
  const resourceLimits = getResourceLimitsForContract(contracts);

  let {
    coresPerServer = 1,
    ramPerServer = 1024,
  } = resourceLimits;
  coresPerServer = coresPerServer < maxCores ? coresPerServer : maxCores;
  ramPerServer = ramPerServer < maxRam ? ramPerServer : maxRam;

  const [, successes] = useNotification({
    actions: [
      ACTIONS_TYPES.DATA_CENTER.CREATE_SERVER,
    ],
  });

  if (successes.length) {
    history.goBack();
  }

  const setServerItems = ({ type, items }) => {
    if (type === 'storages') {
      setStorages([...items]);
    } else if (type === 'cdRoms') {
      setCdRoms([...items]);
    } else if (type === 'nics') {
      setNics([...items]);
    }
  };

  const setServerItem = ({ type, item }) => {
    if (type === 'storages') {
      setStorages((e) => ([...e, item]));
    } else if (type === 'cdRoms') {
      setCdRoms((e) => ([...e, item]));
    } else if (type === 'nics') {
      setNics((e) => ([...e, item]));
    }
  };

  const setFieldServer = (value) => setServer((e) => ({ ...e, ...value }));

  const appendServerItem = ({ type, item }) => {
    const id = server.idCounter + 1;
    setServerItem({ type, item: { id, ...item } });
    setFieldServer({ idCounter: id });
  };

  const getServerItems = (type) => (type === 'storages' ? storages : type === 'cdRoms' ? cdRoms : nics);

  const editServerItem = ({ type, item }) => {
    const items = getServerItems(type);
    const index = items.findIndex(({ id: idInner }) => idInner === item.id);
    items[index] = { ...item, isEdit: false };
    return setServerItems({ type, items });
  };

  const selectToEditServerItem = ({ type, id }) => {
    const items = getServerItems(type);
    const index = items.findIndex(({ id: idInner }) => idInner === id);
    items[index].isEdit = true;
    return setServerItems({ type, items });
  };

  const deselectToEditServerItem = ({ type, id }) => {
    const items = getServerItems(type);
    const index = items.findIndex(({ id: idInner }) => idInner === id);
    items[index].isEdit = false;
    return setServerItems({ type, items });
  };

  const deleteServerItem = ({ type, id }) => {
    const items = getServerItems(type);
    items.splice(items.findIndex(({ id: idInner }) => idInner === id), 1);
    return setServerItems({ type, items });
  };

  const onConfirm = (state) => {
    if (state) {
      const {
        name,
        availabilityZone,
        cpuFamily,
        cores,
        ram,
        type: serverType,
        templateUuid = '',
      } = server;

      const entities = {};

      if (storages.length) {
        entities.volumes = {
          items: [...storages.map(({
            name: _name, size, type, availabilityZone: _availabilityZone, image, imagePassword, sshKeys,
          }) => {
            let extraFields = {
              licenceType: 'LINUX',
            };

            if (image.id) {
              extraFields = {
                image: image.id,
                imagePassword,
              };

              if (image.licenceType === 'LINUX') {
                extraFields.sshKeys = sshKeys;
              }
            }
            return ({
              properties: {
                name: _name,
                size,
                type,
                availabilityZone: _availabilityZone,
                ...extraFields,
              },
            });
          })],
        };
      }

      if (cdRoms.length) {
        entities.cdroms = {
          items: [...cdRoms.map(({ image }) => ({ id: image.id }))],
        };
      }

      if (nics.length) {
        entities.nics = {
          items: [...nics.map(({ lan }) => ({ properties: { lan: lan !== 'new' ? lan : 1 } }))],
        };
      }

      const serverProperties = {
        name,
        ram: ram * 1024,
        cores,
        cpuFamily,
        availabilityZone,
      };

      if (serverType === SERVER_TYPE.CUBE) {
        serverProperties.templateUuid = templateUuid;
        serverProperties.type = SERVER_TYPE.CUBE;
      }

      const serverToProvision = { properties: serverProperties };

      if (Object.keys(entities).length) {
        serverToProvision.entities = entities;
      }

      actions.server.create({ dcId, server: serverToProvision });
    }

    setShowProvisionDialog(false);
  };

  const setCube = ({
    ram, cores, storageSize, id: templateUuid,
  }) => {
    let id = server.idCounter;
    setServerItems({
      type: 'storages',
      items: [
        {
          id: ++id,
          name: _t('Unnamed DAS Storage'),
          availabilityZone: AVAILABILITY_ZONE.AUTO,
          size: storageSize,
          type: STORAGE_TYPE.DAS,
          image: '',
          imagePassword: '',
          sshKeys: '',
        },
      ],
    });

    setFieldServer({
      id,
      name: _t('Unnamed Server'),
      cpuFamily: cpuArchitecture.length ? cpuArchitecture[0].cpuFamily : '',
      availabilityZone: AVAILABILITY_ZONE.AUTO,
      ram: ram / 1024,
      cores,
      type: SERVER_TYPE.CUBE,
      templateUuid,
      idCounter: id,
    });
  };

  const cubeItemClickHandler = (templateId = '') => {
    const template = templates.find(({ id = '' }) => id === templateId);

    if (template) {
      const { id = '', properties: props = {} } = template;
      setCube({ id, ...props });
      setAnchorEl(null);
    }
  };

  const onProvisioningClick = () => {
    const $errors = {};
    if (server.name.length < 3) {
      $errors.name = _t('The value must be at least 3 characters');
    }

    if (!server.name.length) {
      $errors.name = _t('This field is required');
    }

    if (!server.cpuFamily.length) {
      $errors.cpuFamily = _t('This field is required');
    }

    if (Object.keys($errors).length) {
      setValidationErrors($errors);
    } else {
      setShowProvisionDialog(true);
    }
  };

  useEffect(() => {
    if (!contracts.length) {
      actions.contract.getAll();
    }

    if (!hasKeys(datacenter)) {
      actions.dataCenter.get({ id: dcId });
    }
    // eslint-disable-next-line
  }, []);

  const serverPropertiesFields = [
    {
      name: 'name',
      type: 'text',
      onChange: (value) => setFieldServer({ name: value }),
      title: _t('Name'),
    },
    {
      name: 'availabilityZone',
      type: 'dropdown',
      onChange: (value) => setFieldServer({ availabilityZone: value }),
      title: _t('Availability Zone'),
      options: [...enumToArray(AVAILABILITY_ZONE).map((e) => [e, AVAILABILITY_ZONE$[e]])],
    },
    {
      name: 'cpuFamily',
      type: 'dropdown',
      onChange: (value) => setFieldServer({ cpuFamily: value }),
      title: _t('CPU Architecture'),
    },
    {
      name: 'cores',
      onChange: (value) => setFieldServer({ cores: value }),
      title: _t('Cores'),
      type: 'slider',
      range: [1, coresPerServer],
      showValue: true,
    },
    {
      name: 'ram',
      onChange: (value) => setFieldServer({ ram: value }),
      title: _t('RAM'),
      type: 'slider',
      range: [1, (ramPerServer / 1024)],
      showValue: true,
    },
  ];

  const getIsLimitExceeded = (type) => {
    const limit = SERVER_LIMITATION;
    if (type === 'cd-rom') {
      return cdRoms.length < limit.CD_ROMS;
    }

    const isAcc = limit.IS_ACCUMULATIVE;
    const accNums = limit.ACCUMULATIVE_NUMS;
    const used = storages.length + nics.length;
    if (type === 'storage') {
      return !isAcc ? storages.length < limit.HDDS : used < accNums;
    }

    if (type === 'nic') {
      return !isAcc ? nics.length < limit.NICS : used < accNums;
    }

    return 0;
  };

  return (
    <div className="screen-create-server">
      <Paper>
        <h3 className="screen-create-server-title">
          {_t('Properties')}
          {' '}
          <span onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Icon name="bolt" />
            {_t('Cube')}
          </span>
        </h3>

        <Cube
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          onClick={(e) => cubeItemClickHandler(e)}
        />

        <Form
          fields={serverPropertiesFields}
          fieldsValues={server}
          onProps={server}
          isActionButtons={false}
          validationErrors={validationErrors}
          changeFields={[
            { name: 'cores', props: { range: [1, coresPerServer], disabled: server.type === SERVER_TYPE.CUBE } },
            { name: 'ram', props: { range: [1, (ramPerServer / 1024)], disabled: server.type === SERVER_TYPE.CUBE } },
            { name: 'availabilityZone', props: { disabled: server.type === SERVER_TYPE.CUBE } },
            {
              name: 'cpuFamily',
              props: {
                options: cpuArchitecture.map(({ cpuFamily }) => [
                  cpuFamily,
                  <span>
                    <img
                      src={CPU_FAMILY[cpuFamily].logo}
                      style={{ height: 18, marginRight: 10, verticalAlign: 'middle' }}
                      alt={CPU_FAMILY[cpuFamily].name}
                    />
                    {CPU_FAMILY[cpuFamily].name}
                  </span>]),
                disabled: server.type === SERVER_TYPE.CUBE,
              },
            },
          ]}
        />
      </Paper>
      <hr />

      <CreateStorage
        items={storages}
        onAdd={(item) => appendServerItem({ type: 'storages', item })}
        onEdit={(item) => editServerItem({ type: 'storages', item })}
        selectToEdit={(id) => selectToEditServerItem({ type: 'storages', id })}
        selectToDelete={(id) => deleteServerItem({ type: 'storages', id })}
        deselectToEdit={(id) => deselectToEditServerItem({ type: 'storages', id })}
        isLimitExceeded={getIsLimitExceeded('storage')}
      />
      <hr />

      <CreateCdRom
        items={cdRoms}
        onAdd={(item) => appendServerItem({ type: 'cdRoms', item })}
        onEdit={(item) => editServerItem({ type: 'cdRoms', item })}
        selectToEdit={(id) => selectToEditServerItem({ type: 'cdRoms', id })}
        selectToDelete={(id) => deleteServerItem({ type: 'cdRoms', id })}
        deselectToEdit={(id) => deselectToEditServerItem({ type: 'cdRoms', id })}
        isLimitExceeded={getIsLimitExceeded('cd-rom')}
      />
      <hr />

      <CreateNic
        items={nics}
        lans={lanItems.sort((a, b) => (a.id > b.id ? 1 : -1))}
        onAdd={(item) => appendServerItem({ type: 'nics', item })}
        onEdit={(item) => editServerItem({ type: 'nics', item })}
        selectToEdit={(id) => selectToEditServerItem({ type: 'nics', id })}
        selectToDelete={(id) => deleteServerItem({ type: 'nics', id })}
        deselectToEdit={(id) => deselectToEditServerItem({ type: 'nics', id })}
        isLimitExceeded={getIsLimitExceeded('nic')}
      />

      <div className="screen-create-server-provision">
        <span className="button" onClick={onProvisioningClick}>
          <span data-t>Provision Server</span>
        </span>
      </div>

      {showProvisionDialog && (
        <ConfirmDialog
          show
          title="Provision Server"
          message={(
            <>
              <span style={{ display: 'block' }}>Do you really want to Provision is Server?</span>
              <span>
                Our
                {' '}
                <a href="https://www.ionos.com/terms-gtc/index.php?id=66" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                {' '}
                apply
              </span>
            </>
          )}
          onClose={onConfirm}
          yesButtonText="Provision Now"
          noButtonText="Cancel"
        />
      )}

    </div>
  );
};
